import { useEffect, useState } from 'react'

// サンクス流入から表示までの待ち時間。stgの挙動を見て変更する可能性あり（DEV-3392 Figma注記）
const DELAY_MS = 10_000

// DEV-3392 B〜DのLINE連携モーダルの表示タイミング。①流入から10秒後 ②ブラウザバック時 の
// どちらか早い方で1回だけ開く。戻る検知は useGenieeLinePromo と同じ、ダミー履歴を1つ積んで
// popstateで捕まえる方式。
// ただしChrome/Safariは「ユーザー操作なしでpushStateされた履歴」を戻るボタンで飛ばす
// （History Manipulation Intervention）。マウント直後に積むとスマホでスクロールだけして戻ると
// ダミーごと素通りされpopstateが来ないため、ダミーはタップ/キー操作の直後に積む。
// スクロールのみで戻るユーザーは捕まえられない（ブラウザ仕様上の制約）。
// iOS Safariはpointerupをユーザー操作扱いしない場合があるため、touchend/clickでも拾う
const USER_ACTIVATION_EVENTS = ['pointerup', 'touchend', 'click', 'keydown'] as const

type NavigatorWithUserActivation = Navigator & { userActivation?: { isActive: boolean } }

export const useLineLinkModalTrigger = (enabled: boolean) => {
    const [isOpen, setIsOpen] = useState(false)

    useEffect(() => {
        if (!enabled) return

        let hasOpened = false
        let timerId: number | undefined
        const open = (): void => {
            if (hasOpened) return
            hasOpened = true
            window.clearTimeout(timerId)
            setIsOpen(true)
        }

        timerId = window.setTimeout(open, DELAY_MS)

        // 表示前なら、戻る操作はモーダル表示に使われてページに留まる。
        // 表示済みの場合はダミーを消費するだけで「戻るが無反応」になるため、1回だけ素通しさせる
        let hasPushedDummy = false
        const removeActivationListeners = (): void => {
            USER_ACTIVATION_EVENTS.forEach((type) =>
                window.removeEventListener(type, pushDummyOnUserActivation, true)
            )
        }
        const pushDummyOnUserActivation = (): void => {
            if (hasPushedDummy || hasOpened) return
            // スクロール後のpointerup等、ユーザーアクティベーションにならないイベントでは積まない
            const { userActivation } = navigator as NavigatorWithUserActivation
            if (userActivation && !userActivation.isActive) return
            hasPushedDummy = true
            removeActivationListeners()
            window.history.pushState({ lineLinkModal: true }, '')
        }
        USER_ACTIVATION_EVENTS.forEach((type) =>
            window.addEventListener(type, pushDummyOnUserActivation, true)
        )

        let hasPassedThroughBack = false
        const handlePopState = (): void => {
            if (!hasOpened) {
                open()
                return
            }
            if (hasPassedThroughBack) return
            hasPassedThroughBack = true
            window.history.back()
        }
        window.addEventListener('popstate', handlePopState)

        return () => {
            window.clearTimeout(timerId)
            removeActivationListeners()
            window.removeEventListener('popstate', handlePopState)
        }
    }, [enabled])

    return { isOpen, close: () => setIsOpen(false) }
}
